import { Button, InfoModal, useModal } from "@newsela/angelou";
import PropTypes from "prop-types";

import { FACULTY_ADDER_ACTIONS } from "@/admin/hooks/useFacultyAdderReducer";
import {
  modalCss,
  modalFooterCss,
  modalTriggerCss,
} from "./AdminConsoleStepFormModal.style";

const propTypes = {
  /**
   * String value for the admin step form modal eyebrow.
   */
  currentStep: PropTypes.number,
  /**
   * String value for the admin step form modal eyebrow.
   */
  totalSteps: PropTypes.number,
  /**
   * String value for the admin step form modal title.
   */
  title: PropTypes.string,
  /**
   * String value for the admin step form modal trigger button label.
   */
  triggerLabel: PropTypes.string.isRequired,
  /**
   * Object value for the admin step form modal trigger button icon.
   */
  triggerIcon: PropTypes.shape({
    iconNode: PropTypes.node,
    isIconBeforeText: PropTypes.bool,
  }),
  /**
   * Tracking id for the admin step form modal intance.
   */
  id: PropTypes.string.isRequired,
  /**
   * Node that will be rendered as content for the modal form
   * Likely a form.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  /**
   * String value for the admin step form modal cancel or back button.
   */
  cancelBackButtonLabel: PropTypes.string,
  /**
   * Object for the admin step form modal cancel or back button.
   */
  cancelBackButtonIcon: PropTypes.shape({
    iconNode: PropTypes.node,
    isIconBeforeText: PropTypes.bool,
  }),
  /**
   * String value for the admin step form modal next or save button.
   */
  nextSubmitButtonLabel: PropTypes.string,
  /**
   * String value for the admin step form modal next or save button.
   */
  ariaLabelledById: PropTypes.string,
  /**
   * Flag for the admin step form modal next or save button.
   */
  isNextSubmitButtonActive: PropTypes.bool,
  /**
   * Flag to know if should close the admin step form modal next or save button.
   */
  closeAfterClick: PropTypes.bool,
  /**
   * onCancelBack callback method that is executed when the user selects the left button,
   */
  onCancel: PropTypes.func,
  /**
   * onNextSubmit callback method that is executed when the user selects the right button,
   */
  onSubmit: PropTypes.func,
  /**
   * dispatchAction reducer action dispatcher.
   */
  dispatchAction: PropTypes.func,
};

const AdminConsoleStepFormModal = ({
  currentStep,
  totalSteps,
  title,
  triggerLabel,
  triggerIcon,
  id,
  children,
  cancelBackButtonLabel,
  cancelBackButtonIcon,
  nextSubmitButtonLabel,
  ariaLabelledById,
  isNextSubmitButtonActive,
  onCancel,
  onSubmit,
  dispatchAction,
}) => {
  const [modalProps, setActive, triggerRef] = useModal(false);
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;

  const closeModalHandler = () => {
    onCancel();
    setActive(false);
  };

  const onCancelBackHandler = () => {
    if (isFirstStep) {
      onCancel();
      setActive(false);
    } else {
      dispatchAction({
        type: FACULTY_ADDER_ACTIONS.SET_STEP,
        payload: currentStep - 1,
      });
    }
  };

  const onNextSubmitHandler = () => {
    if (isLastStep) {
      onSubmit();
      setActive(false);
    } else {
      dispatchAction({
        type: FACULTY_ADDER_ACTIONS.SET_STEP,
        payload: currentStep + 1,
      });
    }
  };

  return (
    <>
      <Button
        onClick={() => setActive(true)}
        flavor={Button.flavors.primary}
        ref={triggerRef}
        icon={triggerIcon}
        ariaProps={{
          "aria-label": triggerLabel,
        }}
        __cssFor={modalTriggerCss}
      >
        {triggerLabel}
      </Button>
      <InfoModal
        eyebrow={`Step ${currentStep} of ${totalSteps}`}
        title={title}
        labelId={id}
        onClose={closeModalHandler}
        footer={
          <div css={modalFooterCss}>
            <Button
              onClick={onCancelBackHandler}
              flavor={Button.flavors.secondary}
              icon={cancelBackButtonIcon}
              ariaProps={{
                "aria-label": cancelBackButtonLabel,
              }}
            >
              {cancelBackButtonLabel}
            </Button>
            <Button
              onClick={onNextSubmitHandler}
              flavor={Button.flavors.primary}
              disabled={!isNextSubmitButtonActive}
              ariaProps={{
                "aria-label": nextSubmitButtonLabel,
              }}
            >
              {nextSubmitButtonLabel}
            </Button>
          </div>
        }
        {...modalProps}
        __cssFor={modalCss}
      >
        <section>
          <form role="form" aria-labelledby={ariaLabelledById}>
            {children}
          </form>
        </section>
      </InfoModal>
    </>
  );
};

AdminConsoleStepFormModal.propTypes = propTypes;

export default AdminConsoleStepFormModal;
